import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "../styles/SelectBonusRaces.css";

import vallon from "../../medias/png/icons/vallon.png";
import chrono from "../../medias/png/icons/chrono.png";
import plat from "../../medias/png/icons/plat.png";
import montagne from "../../medias/png/icons/montagne.png";

const SelectBonusRace = ({
  selectedRaces,
  setBonusRaces,
  bonusRaces,
  races,
  prevStep,
  saveData,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const icons = {
    Vallon: vallon,
    Chrono: chrono,
    Plat: plat,
    Montagne: montagne,
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const formattedRaces = useMemo(() => {
    return (selectedRaces || [])
      .filter((race) => {
        const [day, month, year] = String(race.date_start).split("-").map(Number);
        const raceDate = new Date(year, month - 1, day);
        return raceDate >= today;
      })
      .map((race) => ({
        ...race,
        date_start: formatDate(race.date_start),
      }));
  }, [selectedRaces, today]);

  const bonusEligibleRaces = useMemo(() => {
    return (races || []).filter((race) => race.groupCompetitionId === 0);
  }, [races]);

  const sortedRaces = useMemo(() => {
    const combinedRaces = [...formattedRaces, ...bonusEligibleRaces];

    const parseDate = (dateString) => {
      const [day, month, year] = String(dateString).split("-").map(Number);
      return new Date(year, month - 1, day);
    };

    return combinedRaces.slice().sort((a, b) => parseDate(a.date_start) - parseDate(b.date_start));
  }, [formattedRaces, bonusEligibleRaces]);

  const [confirmValidDataPopup, setConfirmValidDataPopup] = useState(false);

  const isRaceInBonusRaces = (raceId) => {
    return (bonusRaces || []).some((br) => br.raceId === raceId);
  };

  const handleSelectBonusRace = (race) => {
    const raceId = race?.raceId;
    if (raceId == null) return;

    if (isRaceInBonusRaces(raceId)) {
      setBonusRaces((bonusRaces || []).filter((br) => br.raceId !== raceId));
    } else {
      setBonusRaces([...(bonusRaces || []), race]);
    }
  };

  const isBonusRaceSelected = (raceId) => {
    return (bonusRaces || []).some((br) => br.raceId === raceId);
  };

  const countWT = (bonusRaces || []).filter((race) => race.groupCompetitionId === 0).length;
  const countCont = (bonusRaces || []).filter((race) => race.groupCompetitionId === 1).length;
  const countPro = (bonusRaces || []).filter((race) => race.groupCompetitionId === 2).length;

  const isValidateDisabled = countWT > 3 || countCont > 3 || countPro > 3;

  const divisionLabel = (race) => {
    const g = Number(race?.groupCompetitionId);
    if (g === 0) return t("divisions.worldTour");
    if (g === 1) return t("divisions.continental");
    if (g === 2) return t("divisions.proSeries");
    return t("divisions.international");
  };

  const validData = () => {
    saveData()
      .then(() => setConfirmValidDataPopup(true))
      .catch(() => {});
  };

  const handleClosePopup = () => {
    navigate("/profil");
  };

  return (
    <main className="select-races-bonus">
      {confirmValidDataPopup && (
        <div className="select-races-bonus-popup">
          <div className="select-races-bonus-popup-content">
            <p
              dangerouslySetInnerHTML={{
                __html: t("selectBonusRace.successMessage"),
              }}
            />
            <button onClick={handleClosePopup}>{t("common.confirm")}</button>
          </div>
        </div>
      )}

      <div className="select-races-bonus-shadow-container">
        <div className="select-races-bonus-title">
          <h1>{t("selectBonusRace.title")}</h1>
          <div className="select-races-bonus-shadow-mask-right"></div>
          <div className="select-races-bonus-shadow-mask-bottom"></div>
        </div>
      </div>

      <div className="select-races-bonus-header">
        <div className="select-races-bonus-rules">
          <p>{t("selectBonusRace.rules")}</p>
        </div>

        <div className="select-races-bonus-change-step">
          <button onClick={prevStep}>{t("common.previous")}</button>
          <button onClick={validData} disabled={isValidateDisabled}>
            {t("common.validateUpper")}
          </button>
        </div>
      </div>

      <div className="select-races-bonus-pannel-view">
        <div className="select-races-bonus-choice">
          <div className="select-races-bonus-table-container">
            <table>
              <thead>
                <tr>
                  <th>{t("races.table.race")}</th>
                  <th>{t("races.table.date")}</th>
                  <th>{t("races.table.category")}</th>
                  <th>{t("races.table.division")}</th>
                  <th>{t("races.table.type")}</th>
                  <th>{t("races.table.profile")}</th>
                  <th>{t("races.table.steps")}</th>
                </tr>
              </thead>

              <tbody>
                {sortedRaces.map((race) => (
                  <tr
                    key={race.raceId}
                    onClick={() => handleSelectBonusRace(race)}
                    className={
                      isBonusRaceSelected(race.raceId) ? "select-races-bonus-table-selected-row" : ""
                    }
                  >
                    <td>
                      <div className="select-races-bonus-table-name-container">
                        <img
                          className="select-races-table-flag-icon"
                          src={`${process.env.PUBLIC_URL}/flags/${race.country
                            .normalize("NFD")
                            .replace(/[\u0300-\u036f]/g, "")
                            .replace(/ /g, "_")
                            .toLowerCase()}.png`}
                          alt={t("common.flagAlt", { country: race.country })}
                          width="20"
                          height="20"
                        />
                        <span>{race.name}</span>
                      </div>
                    </td>

                    <td>{race.date_start}</td>
                    <td>{race.category}</td>
                    <td>{divisionLabel(race)}</td>
                    <td>{race.courseType}</td>
                    <td>
                      {icons[race.profil] ? (
                        <img src={icons[race.profil]} alt={race.profil} width="25" height="25" />
                      ) : (
                        race.profil
                      )}
                    </td>
                    <td>{race.step}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="select-races-bonus-choice-view">
          <p className="select-races-bonus-selected-number">
            {t("selectBonusRace.countWT")}{" "}
            <span
              className={countWT === 3 ? "select-races-bonus-selected-races-green" : "select-races-bonus-selected-races-red"}
            >
              {countWT}
            </span>
            /3
          </p>

          <p className="select-races-bonus-selected-number">
            {t("selectBonusRace.countCont")}{" "}
            <span
              className={countCont === 3 ? "select-races-bonus-selected-races-green" : "select-races-bonus-selected-races-red"}
            >
              {countCont}
            </span>
            /3
          </p>

          <p className="select-races-bonus-selected-number">
            {t("selectBonusRace.countPro")}{" "}
            <span
              className={countPro === 3 ? "select-races-bonus-selected-races-green" : "select-races-bonus-selected-races-red"}
            >
              {countPro}
            </span>
            /3
          </p>
        </div>
      </div>
    </main>
  );
};

export default SelectBonusRace;
